// ==============================
// File: src/pages/StorageDebug.jsx
// Works with the new StorageContext API (Folder + Drive):
// - Show current mode, workspace name, last synced/meta & conflicts
// - Pick Folder / Connect Drive / Clear selection
// - Per-file read/write test (defaults to settings/mudoSettings.json)
// - Master Save/Receive (files) and Vault Send/Reload (snapshot)
// - Export backup, Import backup, Health check
// ==============================
import React, { useEffect, useMemo, useState } from "react";
import { useStorage, loadDriveConfigFromLS } from "../contexts/StorageContext";
import { DRIVE_CONFIG_LS_KEY } from "../contexts/StorageContext";
import { useStudio } from "../contexts/StudioContext";

const box = {
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 10,
  padding: 12,
  background: "rgba(255,255,255,0.04)",
};

export default function StorageDebug() {
  const {
    // state
    mode, hasFolder, baseDirName, workspaceName,
    isDriveLinked, isDriveAuthed, driveConfig, busy, lastSynced, lastConflict,

    // link controls
    pickFolder, clearFolderSelection,
    connectDrive, disconnectDrive,

    // file-level API
    loadJSON, saveJSON,

    // master files
    masterSaveFiles, masterReceiveFiles,

    // vault snapshot
    saveAll, reload, receiveFromDrive, forceSend,

    // backup + health
    exportAll, importBackup, healthCheck,
  } = useStorage();

  // Debug render log to confirm hydration usage
  React.useEffect(() => {
    console.log("[StorageDebug] render: isDriveLinked=", isDriveLinked, "driveConfig=", driveConfig);
  }, [isDriveLinked, driveConfig]);

  const { studio } = useStudio();

  function assertDriveConfigLS() {
    const raw = window.localStorage.getItem(DRIVE_CONFIG_LS_KEY);
    console.log("[StorageDebug] assert drive config LS raw", DRIVE_CONFIG_LS_KEY, raw);
    try {
      console.log("[StorageDebug] assert drive config parsed", JSON.parse(raw || "null"));
    } catch (e) {
      console.error("[StorageDebug] assert drive config parse error", e);
    }
    // Also show what loader sees
    try {
      const loaded = loadDriveConfigFromLS?.();
      console.log("[StorageDebug] assert load helper", loaded);
    } catch {}
  }

  async function assertStudioProfile() {
    const raw = window.localStorage.getItem("mudo:studio/studioProfile.json");
    console.log("[StorageDebug] assert studio profile LS raw", raw);
    try {
      console.log("[StorageDebug] assert studio profile LS parsed", JSON.parse(raw || "null"));
    } catch (e) {
      console.error("[StorageDebug] assert studio profile LS parse error", e);
    }
    try {
      const vaultValue = await loadJSON("studio/studioProfile.json", null);
      console.log("[StorageDebug] assert studio profile vault loadJSON", vaultValue);
    } catch (e) {
      console.error("[StorageDebug] assert studio profile vault loadJSON error", e);
    }
  }
  const hasWorkspace = !!hasFolder || (!!isDriveLinked && !!isDriveAuthed);

  // simple tester path
  const [path, setPath] = useState("settings/mudoSettings.json");
  const [value, setValue] = useState("");
  const [loaded, setLoaded] = useState(null);
  const [health, setHealth] = useState(null);
  const [status, setStatus] = useState("");

  // load-on-mount for the default file
  useEffect(() => { doLoad(); /* eslint-disable-next-line */ }, [path, mode, hasFolder, isDriveLinked, isDriveAuthed]);

  async function doLoad() {
    try {
      const data = await loadJSON(path, null);
      setLoaded(data);
      setValue(data ? JSON.stringify(data, null, 2) : "");
      setStatus(`Loaded ${data ? "OK" : "null"}`);
    } catch (e) {
      setLoaded(null);
      setValue("");
      setStatus(`Load error: ${String(e?.message || e)}`);
    }
  }
  async function doSave() {
    try {
      const parsed = value ? JSON.parse(value) : null;
      await saveJSON(path, parsed);
      setStatus("Saved OK");
    } catch (e) {
      setStatus(`Save error: ${String(e?.message || e)}`);
    }
  }

  async function doMasterSave() {
    const r = await masterSaveFiles();
    setStatus(`Master Save (files): wrote ${r?.wrote ?? 0} files`);
  }
  async function doMasterReceive(replaceMode) {
    const r = await masterReceiveFiles({ replaceMode });
    setStatus(
      r?.ok
        ? `Master Receive (files): pulled ${r?.pulled ?? 0} files${replaceMode ? " [replace]" : " [merge]"}`
        : `Master Receive failed: ${r?.reason || "unknown"}`
    );
  }

  async function doSend(force = false) {
    const r = force ? await forceSend() : await saveAll();
    if (r?.conflict && !force) {
      setStatus(
        `CONFLICT: remote ${r.remoteMeta?.updatedAt} is newer than last-synced ${r.lastSynced?.updatedAt}. Click "Send (force)".`
      );
    } else {
      setStatus(
        r?.where
          ? `Sent vault to ${r.where}${r.bytes ? ` • ${r.bytes} bytes` : ""}`
          : "Send failed"
      );
    }
  }
  async function doReload() {
    const r = await receiveFromDrive("manual");
    setStatus(r?.ok ? `Reloaded vault snapshot • keys: ${r.keys}` : `Reload failed: ${r?.reason || "unknown"}`);
  }
  async function doExport() {
    const r = await exportAll();
    setStatus(`Export: ${r?.where || "unknown"} • ${r?.name || ""}`);
  }
  async function doImport(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = await importBackup(f);
    setStatus(`Imported backup • keys: ${r?.keys ?? 0}`);
    e.target.value = "";
  }
  async function doHealth() {
    const r = await healthCheck();
    setHealth(r);
    setStatus(r?.ok ? `Health OK • mode: ${r.mode}` : `Health FAIL • ${r?.reason || "unknown"}`);
  }

  const modeLabel = useMemo(() => {
    if (mode === "localSynced") return "Folder";
    if (mode === "cloudDrive") return "Google Drive";
    return "—";
  }, [mode]);

  return (
    <div style={{ padding: 16, color: "#fff", background: "#0f0f10", minHeight: "100vh" }}>
      <h2 style={{ margin: 0 }}>Storage Debug</h2>
      <p style={{ opacity: 0.75, marginTop: 6 }}>
        Inspect and exercise the current storage backend (Folder or Drive). Uses the new StorageContext API.
      </p>

      {/* Summary */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 8 }}>
        <div style={box}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Workspace</div>
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            Mode: <b>{modeLabel}</b> {busy ? " • (busy…)" : ""}
            <br />
            Name: <b>{workspaceName || "—"}</b>
            <br />
            Base: <b>{baseDirName}</b>
            <br />
            Drive: {isDriveLinked ? (isDriveAuthed ? "linked+authed" : "linked (token pending)") : "not linked"}
                        Drive: {isDriveLinked ? (isDriveAuthed ? "linked+authed" : "linked (token pending)") : "not linked"}
                        {isDriveLinked && driveConfig ? (
                          <>
                            <br />Folder: <code>{driveConfig.folderName || "/Mudo"}</code>
                            <br />Email: <code>{driveConfig.linkedEmail || ""}</code>
                          </>
                        ) : null}
            <br />Studio displayName: <code>{studio?.displayName || studio?.name || "(none)"}</code>
            <br />
            Last synced meta:{" "}
            {lastSynced ? (
              <code style={{ fontSize: 12 }}>{JSON.stringify(lastSynced)}</code>
            ) : (
              <span>—</span>
            )}
            {lastConflict ? (
              <>
                <br />
                <span style={{ color: "#fbbf24" }}>
                  Conflict detected — remote {lastConflict?.remoteMeta?.updatedAt} newer than last-synced {lastConflict?.lastSynced?.updatedAt}
                </span>
              </>
            ) : null}
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
            <button onClick={pickFolder} style={btn()}>Pick Folder…</button>
            <button onClick={pickFolder} style={btn()}>Pick Folder…</button>
            {isDriveLinked ? (
              <button onClick={disconnectDrive} style={btn("warn")}>Disconnect Drive</button>
            ) : (
              <button onClick={connectDrive} style={btn()}>Connect Drive…</button>
            )}
            <button onClick={assertDriveConfigLS} style={btn("info")}>Assert Drive Config in localStorage</button>
            <button onClick={assertStudioProfile} style={btn("info")}>Assert Studio Profile</button>
            {(hasFolder || isDriveLinked) && (
              <button onClick={clearFolderSelection} style={btn("danger")}>Clear selection</button>
            )}
          </div>
        </div>

        <div style={box}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Backup & Health</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={doExport} style={btn()}>Export backup</button>
            <label style={btn()}>
              Import backup
              <input type="file" accept="application/json" onChange={doImport} style={{ display: "none" }} />
            </label>
            <button onClick={doHealth} style={btn()}>Health check</button>
          </div>
          <div style={{ fontSize: 12, opacity: 0.8, marginTop: 8 }}>
            Health: {health ? <code>{JSON.stringify(health)}</code> : "—"}
          </div>
        </div>
      </div>

      {/* File I/O */}
      <div style={{ ...box, marginTop: 12 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Per-file Test (loadJSON / saveJSON)</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "center" }}>
          <input
            style={input()}
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder="e.g. materials.json or settings/mudoSettings.json"
          />
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={doLoad} style={btn()}>Load</button>
            <button onClick={doSave} style={btn("primary")}>Save</button>
          </div>
        </div>
        <div style={{ marginTop: 8 }}>
          <textarea
            style={area()}
            spellCheck={false}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="JSON value…"
          />
        </div>
        <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
          Last loaded preview:{" "}
          <code>{safePreview(loaded)}</code>
        </div>
      </div>

      {/* Master Files vs Vault snapshot */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
        <div style={box}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Master Save/Receive (Files)</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={doMasterSave} style={btn("primary")}>Master Save (files)</button>
            <button onClick={() => doMasterReceive(true)} style={btn()}>Master Receive (replace)</button>
            <button onClick={() => doMasterReceive(false)} style={btn()}>Master Receive (merge)</button>
          </div>
          <div style={{ fontSize: 12, opacity: 0.75, marginTop: 8 }}>
            Uses known DOMAIN_FILES. Save pulls from localStorage mirrors (mudo:&lt;path&gt;); Receive writes to current storage and mirrors locally.
          </div>
        </div>

        <div style={box}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Vault Snapshot (Send / Reload)</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={() => doSend(false)} style={btn("primary")}>Send</button>
            <button onClick={() => doSend(true)} style={btn("warn")}>Send (force)</button>
            <button onClick={doReload} style={btn()}>Reload</button>
          </div>
          <div style={{ fontSize: 12, opacity: 0.75, marginTop: 8 }}>
            Send builds a snapshot from all localStorage keys (except internal) and writes to <code>vault.json</code>. Reload restores from it.
          </div>
        </div>
      </div>

      {/* Status */}
      <div style={{ ...box, marginTop: 12 }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Status</div>
        <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12, whiteSpace: "pre-wrap" }}>
          {status || "—"}
        </div>
      </div>
    </div>
  );
}

/* ---------- tiny helpers ---------- */
function safePreview(v) {
  try { return JSON.stringify(v); } catch { return String(v); }
}
function btn(kind) {
  const base = {
    padding: "8px 12px",
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.08)",
    color: "#fff",
    fontSize: 13,
    cursor: "pointer",
  };
  if (kind === "primary") return { ...base, background: "rgba(37,99,235,0.22)", borderColor: "rgba(37,99,235,0.35)" };
  if (kind === "warn") return { ...base, background: "rgba(251,191,36,0.25)", borderColor: "rgba(251,191,36,0.35)" };
  if (kind === "danger") return { ...base, background: "rgba(239,68,68,0.18)", borderColor: "rgba(239,68,68,0.45)" };
  return base;
}
function input() {
  return {
    padding: "8px 10px",
    minWidth: 200,
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.06)",
    color: "#fff",
    outline: "none",
    fontSize: 13,
    width: "100%",
  };
}
function area() {
  return {
    ...input(),
    minHeight: 160,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 12,
    lineHeight: 1.5,
    resize: "vertical",
  };
}
